/**
 * VALOR POR EXTENSO em português, para a folha e o comprovante da premiação — a mesma conta do 1.0
 * (`public/js/extenso.js`), com os mesmos casos de teste.
 *
 * Não é enfeite: o extenso é o que trava adulteração de caneta no papel — quem recebe confere a
 * frase contra o algarismo. Por isso nunca sai vazio nem aproximado. O que o português cobra e um
 * `Intl` não resolve: cem × cento, os 10-19, o "e" antes da última parcela (mas não depois do mil)
 * e o singular de real/centavo.
 */

const UNI = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
const DEZ_A_DEZENOVE = [
  'dez',
  'onze',
  'doze',
  'treze',
  'quatorze',
  'quinze',
  'dezesseis',
  'dezessete',
  'dezoito',
  'dezenove',
];
const DEZENAS = [
  '',
  '',
  'vinte',
  'trinta',
  'quarenta',
  'cinquenta',
  'sessenta',
  'setenta',
  'oitenta',
  'noventa',
];
// "cem" só existe sozinho; com resto vira "cento".
const CENTENAS = [
  '',
  'cento',
  'duzentos',
  'trezentos',
  'quatrocentos',
  'quinhentos',
  'seiscentos',
  'setecentos',
  'oitocentos',
  'novecentos',
];

function ate999(n: number): string {
  if (n === 100) return 'cem';
  const c = Math.floor(n / 100);
  const resto = n % 100;
  const partes: string[] = [];
  if (c) partes.push(CENTENAS[c] ?? '');
  if (resto >= 10 && resto <= 19) partes.push(DEZ_A_DEZENOVE[resto - 10] ?? '');
  else {
    const d = Math.floor(resto / 10);
    const u = resto % 10;
    if (d) partes.push(DEZENAS[d] ?? '');
    if (u) partes.push(UNI[u] ?? '');
  }
  return partes.join(' e ');
}

/** 0..999.999 (a premiação semanal não chega perto do milhão). */
export function inteiroPorExtenso(n: number): string {
  if (n === 0) return 'zero';
  if (n < 1000) return ate999(n);
  const milhares = Math.floor(n / 1000);
  const resto = n % 1000;
  const cabeca = milhares === 1 ? 'mil' : `${ate999(milhares)} mil`;
  if (!resto) return cabeca;
  // o "e" depois do mil só entra quando o resto é uma parcela só: 1500 = "mil e quinhentos",
  // 5239 = "cinco mil duzentos e trinta e nove"
  const ligaComE = resto < 100 || resto % 100 === 0;
  return cabeca + (ligaComE ? ' e ' : ' ') + ate999(resto);
}

/** 501.77 → "quinhentos e um reais e setenta e sete centavos". */
export function valorPorExtenso(valor: number): string {
  // arredonda no CENTAVO antes de tudo: o papel tem que bater com o algarismo impresso
  const centavosTotais = Math.round(Math.abs(Number(valor) || 0) * 100);
  const reais = Math.floor(centavosTotais / 100);
  const centavos = centavosTotais % 100;
  const partes: string[] = [];
  if (reais > 0) partes.push(`${inteiroPorExtenso(reais)} ${reais === 1 ? 'real' : 'reais'}`);
  if (centavos > 0)
    partes.push(`${inteiroPorExtenso(centavos)} ${centavos === 1 ? 'centavo' : 'centavos'}`);
  if (!partes.length) return 'zero reais';
  return partes.join(' e ');
}
